import { Editor } from "@monaco-editor/react";
import { PlayArrow as PlayArrowIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useEvaluateMagic, useMagicEvents, useMagicOutputs } from "../hooks/use-magic.ts";
import type { MagicDefinition, MagicEvalOutput } from "../types/magic.ts";
import { clickableProps } from "../utils/keyboard";
import { RunStatusChip } from "./run-status-chip.tsx";

interface MagicSidePanelProps {
  selectedDefinition: MagicDefinition | null;
  onClose?: () => void;
  onSelectDefinitionByName?: (name: string, output?: MagicEvalOutput) => void;
  selectedEvalOutput?: MagicEvalOutput | null;
}

const MagicSidePanel: React.FC<MagicSidePanelProps> = ({
  selectedDefinition,
  onClose,
  onSelectDefinitionByName,
  selectedEvalOutput,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [evaluatingEventId, setEvaluatingEventId] = useState<string | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  // Pagination state
  const [eventsPage, setEventsPage] = useState(0);
  const [eventsRowsPerPage, setEventsRowsPerPage] = useState(10);
  const [runsPage, setRunsPage] = useState(0);
  const [runsRowsPerPage, setRunsRowsPerPage] = useState(10);

  const { data: events, isLoading: isLoadingEvents } = useMagicEvents(
    selectedDefinition?.name,
    selectedDefinition?.x_id,
    eventsRowsPerPage,
    eventsPage * eventsRowsPerPage,
  );

  const { data: outputs, isLoading: isLoadingOutputs } = useMagicOutputs(
    undefined,
    selectedDefinition?.name,
    runsRowsPerPage,
    runsPage * runsRowsPerPage,
  );

  const { mutate: evaluate, isPending: isEvaluating, data: evalResult, reset } = useEvaluateMagic();

  // Reset state when definition changes
  useEffect(() => {
    setTabValue(0);
    setEvaluatingEventId(null);
    setExpandedEventId(null);
    setExpandedRunId(null);
    setEventsPage(0);
    setRunsPage(0);
    reset();
  }, [reset]);

  const handleEvaluate = useCallback(
    (eventId: string) => {
      setEvaluatingEventId(eventId);
      reset(); // Clear previous results
      evaluate({ ev_id: eventId });
    },
    [reset, evaluate],
  );

  const handleRowClick = useCallback(
    (eventId: string) => {
      // Reset eval result when changing event selection
      if (expandedEventId !== eventId) {
        reset();
        setEvaluatingEventId(null);
      }
      setExpandedEventId(expandedEventId === eventId ? null : eventId);
    },
    [expandedEventId, reset],
  );

  const eventsList = useMemo(() => {
    return (
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Event ID</TableCell>
                <TableCell>Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!events || events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography variant="caption" color="text.secondary">
                      No events on this page
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => {
                  const isExpanded = expandedEventId === event.id;
                  return (
                    <React.Fragment key={event.id}>
                      <TableRow
                        {...clickableProps(() => handleRowClick(event.id))}
                        onClick={() => handleRowClick(event.id)}
                        sx={{
                          cursor: "pointer",
                          backgroundColor: isExpanded ? "action.hover" : "inherit",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        <TableCell>
                          <Tooltip title={event.id} placement="top">
                            <Typography variant="caption" fontFamily="monospace">
                              {event.id.substring(0, 8)}...
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {new Date(event.ts / 1_000_000).toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            sx={{ backgroundColor: "background.default", p: 2 }}
                          >
                            <Stack spacing={2}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" gutterBottom>
                                  Event Data:
                                </Typography>
                                <Paper
                                  variant="outlined"
                                  sx={{
                                    p: 1,
                                    backgroundColor: "background.paper",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    fontFamily="monospace"
                                    component="pre"
                                    sx={{
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                      margin: 0,
                                    }}
                                  >
                                    {JSON.stringify(event.data, null, 2)}
                                  </Typography>
                                </Paper>
                              </Box>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<PlayArrowIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEvaluate(event.id);
                                }}
                                disabled={isEvaluating}
                                color={evaluatingEventId === event.id ? "primary" : "inherit"}
                              >
                                {isEvaluating && evaluatingEventId === event.id
                                  ? "Evaluating..."
                                  : "Evaluate Event"}
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={-1}
          page={eventsPage}
          onPageChange={(_, newPage) => setEventsPage(newPage)}
          rowsPerPage={eventsRowsPerPage}
          onRowsPerPageChange={(e) => {
            setEventsRowsPerPage(parseInt(e.target.value, 10));
            setEventsPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelDisplayedRows={({ from, to }) => `${from}-${to} (showing latest events)`}
        />
      </Paper>
    );
  }, [
    events,
    evaluatingEventId,
    isEvaluating,
    expandedEventId,
    eventsPage,
    eventsRowsPerPage,
    handleEvaluate,
    handleRowClick,
  ]);

  const evaluationResults = useMemo(() => {
    if (!evalResult) return null;

    return (
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Dry-Run Evaluation Status
          </Typography>
          <RunStatusChip status={evalResult.status} />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {(evalResult.elapsed_ns / 1_000_000).toFixed(2)}ms
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Execution Info
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>X ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Time (ms)</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {evalResult.exec_info.map((info, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Tooltip title={info.x_id} placement="top">
                        <Typography variant="caption" fontFamily="monospace">
                          {info.x_id.substring(0, 8)}...
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <RunStatusChip status={info.status} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {(info.elapsed_ns / 1_000_000).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={info.error || info.message || "-"} placement="top">
                        <Typography
                          variant="caption"
                          color={info.error ? "error" : "text.secondary"}
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                          }}
                        >
                          {info.error || info.message || "-"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>
    );
  }, [evalResult]);

  const handleRunRowClick = useCallback(
    (runId: string, name: string, output: MagicEvalOutput) => {
      const willExpand = expandedRunId !== runId;
      setExpandedRunId(willExpand ? runId : null);

      // Auto-select the name subgraph when expanding a run and show the output
      if (willExpand && onSelectDefinitionByName) {
        onSelectDefinitionByName(name, output);
      }
    },
    [expandedRunId, onSelectDefinitionByName],
  );

  const runsList = useMemo(() => {
    return (
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Run ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Time (ms)</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!outputs || outputs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="caption" color="text.secondary">
                      No runs on this page
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                outputs.map((output) => {
                  const isExpanded = expandedRunId === output.id;
                  return (
                    <React.Fragment key={output.id}>
                      <TableRow
                        {...clickableProps(() => handleRunRowClick(output.id, output.name, output))}
                        onClick={() => handleRunRowClick(output.id, output.name, output)}
                        sx={{
                          cursor: "pointer",
                          backgroundColor: isExpanded ? "action.hover" : "inherit",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        <TableCell>
                          <Tooltip title={output.id} placement="top">
                            <Typography variant="caption" fontFamily="monospace">
                              {output.id.substring(0, 8)}...
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <RunStatusChip status={output.output.status} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {(output.output.elapsed_ns / 1_000_000).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {new Date(output.created_at).toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{ backgroundColor: "background.default", p: 2 }}
                          >
                            <Stack spacing={2}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" gutterBottom>
                                  Execution Info:
                                </Typography>
                                <TableContainer component={Paper} variant="outlined">
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>X ID</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Time (ms)</TableCell>
                                        <TableCell>Message</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {output.output.exec_info.map((info, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell>
                                            <Tooltip title={info.x_id} placement="top">
                                              <Typography variant="caption" fontFamily="monospace">
                                                {info.x_id.substring(0, 8)}...
                                              </Typography>
                                            </Tooltip>
                                          </TableCell>
                                          <TableCell>
                                            <RunStatusChip status={info.status} />
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="caption">
                                              {(info.elapsed_ns / 1_000_000).toFixed(2)}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Tooltip
                                              title={info.error || info.message || "-"}
                                              placement="top"
                                            >
                                              <Typography
                                                variant="caption"
                                                color={info.error ? "error" : "text.secondary"}
                                                sx={{
                                                  maxWidth: 200,
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                  display: "block",
                                                }}
                                              >
                                                {info.error || info.message || "-"}
                                              </Typography>
                                            </Tooltip>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={-1}
          page={runsPage}
          onPageChange={(_, newPage) => setRunsPage(newPage)}
          rowsPerPage={runsRowsPerPage}
          onRowsPerPageChange={(e) => {
            setRunsRowsPerPage(parseInt(e.target.value, 10));
            setRunsPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelDisplayedRows={({ from, to }) => `${from}-${to} (showing latest runs)`}
        />
      </Paper>
    );
  }, [outputs, expandedRunId, runsPage, runsRowsPerPage, handleRunRowClick]);

  if (!selectedDefinition) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box p={2}>
          <Typography variant="h6">Magic Overview</Typography>
        </Box>
        <Divider />
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="Evaluation Runs" />
            <Tab label="Help" />
          </Tabs>
        </Box>
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {tabValue === 0 && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">All Runs</Typography>
              {isLoadingOutputs ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <CircularProgress size={16} />
                  <Typography variant="caption">Loading runs...</Typography>
                </Stack>
              ) : (
                <>
                  {runsList}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Click a row to expand and see execution details
                  </Typography>
                </>
              )}
            </Stack>
          )}
          {tabValue === 1 && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Click on any node in the graph to view its events and evaluate them.
              </Typography>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Features:
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  • View definition details and dependencies
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  • Browse events for each definition
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  • Evaluate individual events (dry-run)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  • View evaluation runs by definition name
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {onClose && (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" p={2}>
            <Typography variant="h6">Definition Details</Typography>
            <Button size="small" onClick={onClose}>
              Close
            </Button>
          </Stack>
          <Divider />
        </>
      )}
      {!onClose && (
        <>
          <Box p={2}>
            <Typography variant="h6">Definition Details</Typography>
          </Box>
          <Divider />
        </>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Details" />
          <Tab label="Events" />
          <Tab label="Runs" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        {tabValue === 0 && (
          <Stack spacing={3}>
            {/* Definition Info */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Definition
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Name:
                      </Typography>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontWeight="bold"
                        sx={{
                          bgcolor: "action.hover",
                          px: 1,
                          py: 0.5,
                          borderRadius: 0.5,
                        }}
                      >
                        {selectedDefinition.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        X ID:
                      </Typography>
                      <Tooltip title={selectedDefinition.x_id} placement="top">
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          sx={{
                            bgcolor: "action.hover",
                            px: 1,
                            py: 0.5,
                            borderRadius: 0.5,
                          }}
                        >
                          {selectedDefinition.x_id}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    <Chip
                      label={selectedDefinition.type}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={selectedDefinition.source}
                      size="small"
                      variant={selectedDefinition.source === "code" ? "outlined" : "filled"}
                      color={selectedDefinition.source === "code" ? "default" : "secondary"}
                    />
                    {selectedDefinition.conditions &&
                      selectedDefinition.conditions.length > 0 &&
                      selectedDefinition.conditions[0].timeout_ms && (
                        <Chip
                          label={`Timeout: ${selectedDefinition.conditions[0].timeout_ms}ms`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                  </Stack>
                  {selectedDefinition.description && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Description
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                        {selectedDefinition.description}
                      </Typography>
                    </Box>
                  )}
                  {selectedDefinition.filter && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Filter:
                      </Typography>
                      <Box
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <Editor
                          height="60px"
                          defaultLanguage="python"
                          value={selectedDefinition.filter}
                          options={{
                            readOnly: true,
                            minimap: { enabled: false },
                            lineNumbers: "on",
                            folding: false,
                            scrollbar: {
                              vertical: "auto",
                              horizontal: "auto",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                  {selectedDefinition.validator && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Validator:
                      </Typography>
                      <Box
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <Editor
                          height="60px"
                          defaultLanguage="python"
                          value={selectedDefinition.validator}
                          options={{
                            readOnly: true,
                            minimap: { enabled: false },
                            lineNumbers: "on",
                            folding: false,
                            scrollbar: {
                              vertical: "auto",
                              horizontal: "auto",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                  {selectedDefinition.dep_ids && selectedDefinition.dep_ids.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Dependencies:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                        {selectedDefinition.dep_ids.map((depId) => (
                          <Tooltip key={depId} title={depId} placement="top">
                            <Chip label={depId} size="small" variant="outlined" />
                          </Tooltip>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Box>

            {/* Evaluation Output (if selected from runs) */}
            {selectedEvalOutput && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Evaluation Result
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Status:
                      </Typography>
                      <RunStatusChip status={selectedEvalOutput.output.status} />
                      <Typography variant="caption" color="text.secondary">
                        Time: {(selectedEvalOutput.output.elapsed_ns / 1_000_000).toFixed(2)}
                        ms
                      </Typography>
                    </Stack>

                    <Box>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Execution Info:
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>X ID</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Time (ms)</TableCell>
                              <TableCell>Message</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedEvalOutput.output.exec_info.map((info, idx) => (
                              <TableRow key={idx}>
                                <TableCell>
                                  <Typography variant="caption" fontFamily="monospace">
                                    {info.x_id}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <RunStatusChip status={info.status} />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption">
                                    {(info.elapsed_ns / 1_000_000).toFixed(2)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption">
                                    {info.message || info.error || "-"}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Stack>
                </Paper>
              </Box>
            )}
          </Stack>
        )}

        {tabValue === 1 && (
          <Stack spacing={2}>
            {/* Events Section */}
            <Typography variant="subtitle2">Events</Typography>
            {isLoadingEvents ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="caption">Loading events...</Typography>
              </Stack>
            ) : (
              <>
                {eventsList}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Click a row to expand and see event data
                </Typography>
              </>
            )}

            {evaluationResults}
          </Stack>
        )}

        {tabValue === 2 && (
          <Stack spacing={2}>
            {/* Runs Section */}
            <Typography variant="subtitle2">Evaluation Runs ({selectedDefinition.name})</Typography>
            {isLoadingOutputs ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="caption">Loading runs...</Typography>
              </Stack>
            ) : (
              <>
                {runsList}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Click a row to expand and see execution details
                </Typography>
              </>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default MagicSidePanel;
